// Marina Bay shell: loading overlay, camera rig (three.js camera for the MATHS only; gestures are our own), RAF loop.
// The renderer is Rust/wgpu in wasm; this file never draws.
//
// LENS SHIFT (ARCHITECTURE.md §1): the hero frame is a zero-pitch camera whose principal point sits 17 % from
// the top (NDC y = +0.659), so verticals stay vertical. The rig keeps that look while orbiting:
//   · The rig orbits a PIVOT ON THE GROUND (y = 0) — the point at the centre of the frame.
//   · From the rig's elevation e the render camera gets   shift = min(S · fade(e), tan e / tan(fov/2))
//     and   pitch = e − atan(shift · tan(fov/2)),   which keeps the pivot at the frame centre. Low views are
//     un-pitched and fully shifted (the hero is exactly pitch 0, shift 0.659); steep views ease to a plain
//     pitched camera (fade: 25° → 60°).
//   · camera.quaternion / projectionMatrix(+Inverse) are overwritten with the render camera every frame, so
//     unprojects (zoom-to-cursor, fly-to) use exactly what Rust renders.
// GESTURES: left-drag / one finger orbit · right-drag, shift-drag / two fingers pan · wheel / pinch zoom to the
// cursor · double-click / double-tap fly-to · H or the ⟲ button back to the hero view. Everything is damped.
// A 5 s cinematic intro eases into the hero view; after 20 s without input the camera drifts a few degrees.
import { MathUtils, PerspectiveCamera, Vector3 } from 'three';

type Vec3 = [number, number, number];
interface Hero { pos: Vec3; target: Vec3; fov: number; shiftY: number; near: number }
interface Engine {
  setCamera(view: Float32Array, pos: Float32Array, fovDeg: number, near: number, shiftY: number): void;
  setTime(seconds: number): void;
  setOptions(json: string): void;
  resize(cssW: number, cssH: number, dpr: number): void;
  render(nowMs: number): string;
  stats(): string;
  hero(): string;
  frameDone(): Promise<void>;
  stop(): void;
}
declare global {
  interface Window {
    __sceneReady?: boolean;
    __SCENE_ERRORS__: string[];
    __SCENE_READY_MS__?: number;
    __marinabay?: { engine: Engine; camera: PerspectiveCamera; controls: Rig; stats: () => unknown };
  }
}

const params = new URLSearchParams(location.search);
const canvas = document.getElementById('world') as HTMLCanvasElement;
const loading = document.getElementById('loading') as HTMLElement;
const loadingBar = document.getElementById('loading-bar') as HTMLElement;
const loadingStatus = document.getElementById('loading-status') as HTMLElement;
const loadingProgress = document.getElementById('loading-progress') as HTMLElement;
const statsEl = document.getElementById('stats') as HTMLElement;
const resetButton = document.getElementById('reset-view') as HTMLButtonElement;
window.__SCENE_ERRORS__ = [];

const shot = params.get('shot') === '1';
if (shot) document.body.classList.add('shot');
const showStats = params.get('stats') === '1';

function progress(text: string, pct: number): void {
  loadingStatus.textContent = text;
  loadingBar.style.transform = `scaleX(${Math.min(1, Math.max(0, pct / 100))})`;
  loadingProgress.setAttribute('aria-valuenow', String(Math.round(pct)));
}
function fail(message: string): void {
  window.__SCENE_ERRORS__.push(message);
  loading.classList.remove('done', 'busy');
  loading.classList.add('error');
  loading.setAttribute('aria-busy', 'false');
  loadingStatus.textContent = message;
  console.error(`[marinabay] ${message}`);
}

/* ------------------------------------------------------------------ url helpers */
const num = (key: string): number | null => {
  const v = params.get(key);
  if (v === null || v.trim() === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const vec3 = (key: string): Vec3 | null => {
  const parts = (params.get(key) ?? '').split(',').map(Number);
  return parts.length === 3 && parts.every(Number.isFinite) ? (parts as Vec3) : null;
};
const off = (key: string): boolean => ['off', '0', 'false', 'none'].includes((params.get(key) ?? '').toLowerCase());

const coarsePointer = matchMedia('(pointer: coarse), (hover: none)').matches;
const quality = (params.get('quality') ?? (coarsePointer ? 'low' : 'high')).toLowerCase() === 'low' ? 'low' : 'high';
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

function renderDpr(): number {
  const forced = num('dpr');
  if (forced !== null && forced > 0) return Math.min(4, Math.max(0.5, forced));
  return Math.min(quality === 'low' ? 1.25 : 2, window.devicePixelRatio || 1);
}

/* ------------------------------------------------------------------ named views (optical-axis target, explicit shift) */
const NAMED_VIEWS: Record<string, { pos: Vec3; target: Vec3; fov?: number; shift?: number }> = {
  orbit: { pos: [500, 220, 700], target: [0, 40, 100] },
  water: { pos: [-40, 26, 760], target: [20, 40, 300], shift: 0.2 },
  gardens: { pos: [-620, 210, 620], target: [-300, 20, 120] },
  top: { pos: [0, 1500, 900], target: [0, 0, 300] },
};

/* ------------------------------------------------------------------ camera rig */
const FADE_START = MathUtils.degToRad(25);
const FADE_END = MathUtils.degToRad(60);
const MIN_ELEVATION = MathUtils.degToRad(2);
const MAX_ELEVATION = MathUtils.degToRad(86);
const MIN_DISTANCE = 90;
const MAX_DISTANCE = 2600;
const TARGET_LIMIT = 2600;
const IDLE_AFTER_MS = 20_000;

interface Pose { target: Vector3; azimuth: number; elevation: number; distance: number }
const clonePose = (p: Pose): Pose => ({ target: p.target.clone(), azimuth: p.azimuth, elevation: p.elevation, distance: p.distance });
const ease = (u: number): number => u * u * u * (u * (u * 6 - 15) + 10); // smootherstep

class Rig {
  readonly camera = new PerspectiveCamera(41.85, 4 / 3, 1, 60000);
  /** maximum lens shift of this session (the hero's, or `?shift=`) */
  maxShift = 0.659;
  shift = 0;
  near = 4;
  /** true once the user has moved the camera away from the opening view */
  moved = false;
  onMovedChange: (moved: boolean) => void = () => {};
  /** until the first interaction the camera is EXACTLY the explicit one (hero / ?cam) */
  private explicit: { pos: Vector3; target: Vector3; shift: number } | null = null;
  private home: { pos: Vector3; target: Vector3; shift: number; pose: Pose } | null = null;
  private pose: Pose = { target: new Vector3(), azimuth: 0, elevation: 0.3, distance: 900 };
  private goal: Pose = clonePose(this.pose);
  /** a timed move between two poses (intro, fly-to, reset); `land` = snap to the explicit camera at the end */
  private flight: { start: number; duration: number; from: Pose; to: Pose; land: boolean } | null = null;
  private idle: { since: number; base: Pose | null } = { since: performance.now(), base: null };
  idleDrift = true;
  private readonly pointers = new Map<number, { x: number; y: number; button: number; shift: boolean }>();
  private pinch: { dist: number; cx: number; cy: number } | null = null;
  private lastTap = { t: 0, x: 0, y: 0 };
  private lastUpdate = 0;
  private readonly tmp = new Vector3();
  private readonly fwd = new Vector3();

  constructor(private readonly dom: HTMLCanvasElement) {
    dom.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
    dom.addEventListener('pointerdown', (e) => this.onDown(e));
    dom.addEventListener('pointermove', (e) => this.onMove(e));
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) dom.addEventListener(type, (e) => this.onUp(e));
    dom.addEventListener('contextmenu', (e) => e.preventDefault());
    dom.addEventListener('dblclick', (e) => { e.preventDefault(); this.flyTo(e.clientX, e.clientY); });
  }

  /** compatibility with the old OrbitControls handle: `__marinabay.controls.target` */
  get target(): Vector3 { return this.pose.target; }
  get tanHalf(): number { return Math.tan(MathUtils.degToRad(this.camera.fov) / 2); }

  private static poseOf(pos: Vector3, pivot: Vector3): Pose {
    const d = pos.clone().sub(pivot);
    const distance = Math.max(d.length(), 1e-3);
    return { target: pivot.clone(), azimuth: Math.atan2(d.x, d.z), elevation: Math.asin(MathUtils.clamp(d.y / distance, -1, 1)), distance };
  }
  private static position(p: Pose, out: Vector3): Vector3 {
    const c = Math.cos(p.elevation);
    return out.set(p.target.x + p.distance * c * Math.sin(p.azimuth), p.target.y + p.distance * Math.sin(p.elevation), p.target.z + p.distance * c * Math.cos(p.azimuth));
  }

  /** An explicit camera: optical axis toward `target`, lens shift `shift`. The orbit pivot becomes the ground
   *  point at the centre of the frame, so taking over by hand starts from the very same picture. */
  setExplicit(pos: Vec3, target: Vec3, fov: number, shift: number, asHome = true): void {
    this.camera.fov = fov;
    this.maxShift = Math.max(0, shift);
    const p = new Vector3(...pos);
    const t = new Vector3(...target);
    // frame-centre ray = optical axis tilted down by atan(shift · tan(fov/2))
    const axis = t.clone().sub(p).normalize();
    const right = new Vector3().crossVectors(axis, new Vector3(0, 1, 0)).normalize();
    const up = new Vector3().crossVectors(right, axis).normalize();
    const centre = axis.clone().addScaledVector(up, -shift * this.tanHalf).normalize();
    let pivot: Vector3;
    if (centre.y < -0.02 && p.y > 0) {
      const d = Math.min(-p.y / centre.y, MAX_DISTANCE);
      pivot = p.clone().addScaledVector(centre, d);
      pivot.y = 0;
    } else {
      pivot = new Vector3(p.x + axis.x * 600, 0, p.z + axis.z * 600);
    }
    this.explicit = { pos: p, target: t, shift };
    this.pose = Rig.poseOf(p, pivot);
    this.goal = clonePose(this.pose);
    if (asHome) this.home = { pos: p.clone(), target: t.clone(), shift, pose: clonePose(this.pose) };
  }

  /** 5 s ease from a slightly higher, wider, turned pose into the opening view */
  startIntro(duration: number): void {
    if (!this.home) return;
    const to = clonePose(this.home.pose);
    const from = clonePose(to);
    from.distance *= 1.2;
    from.elevation = Math.min(MAX_ELEVATION, to.elevation + MathUtils.degToRad(4.5));
    from.azimuth -= MathUtils.degToRad(7);
    this.explicit = null;
    this.flight = { start: performance.now(), duration, from, to, land: true };
  }

  /** back to the opening view (H key, ⟲ button) */
  reset(): void {
    if (!this.home) return;
    const to = clonePose(this.home.pose);
    // take the short way round
    to.azimuth = this.pose.azimuth + Math.atan2(Math.sin(to.azimuth - this.pose.azimuth), Math.cos(to.azimuth - this.pose.azimuth));
    this.explicit = null;
    this.flight = { start: performance.now(), duration: reducedMotion ? 1 : 1400, from: clonePose(this.pose), to, land: true };
    this.touch(false);
  }

  /** ground point under a client position, through the very matrices Rust renders with */
  private ground(clientX: number, clientY: number): Vector3 | null {
    const rect = this.dom.getBoundingClientRect();
    const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
    const ndcY = 1 - ((clientY - rect.top) / rect.height) * 2;
    const origin = this.camera.position;
    const dir = this.tmp.set(ndcX, ndcY, 0.5).unproject(this.camera).sub(origin).normalize();
    if (dir.y > -1e-3) return null;
    const hit = -origin.y / dir.y;
    if (hit > 5000) return null;
    const g = origin.clone().addScaledVector(dir, hit);
    g.x = MathUtils.clamp(g.x, -TARGET_LIMIT, TARGET_LIMIT);
    g.z = MathUtils.clamp(g.z, -TARGET_LIMIT, TARGET_LIMIT);
    return g;
  }

  private flyTo(clientX: number, clientY: number): void {
    const g = this.ground(clientX, clientY);
    if (!g) return;
    this.takeOver();
    const to = clonePose(this.goal);
    to.target.copy(g);
    to.distance = MathUtils.clamp(this.pose.distance * 0.5, MIN_DISTANCE * 1.5, MAX_DISTANCE);
    to.elevation = MathUtils.clamp(Math.max(to.elevation, MathUtils.degToRad(16)), MIN_ELEVATION, MAX_ELEVATION);
    this.flight = { start: performance.now(), duration: reducedMotion ? 1 : 1300, from: clonePose(this.pose), to, land: false };
  }

  /** hand the camera to the user: from here on the rig rule drives shift and pitch */
  private takeOver(): void {
    this.explicit = null;
    if (this.flight) {
      this.goal = clonePose(this.pose);
      this.flight = null;
    }
    this.touch(true);
  }
  private touch(moved: boolean): void {
    this.idle.since = performance.now();
    if (this.idle.base) {
      // leave the drift where it is: no jump
      this.goal = clonePose(this.pose);
      this.idle.base = null;
    }
    if (moved !== this.moved) {
      this.moved = moved;
      this.onMovedChange(moved);
    }
  }

  private zoomAbout(factor: number, clientX: number, clientY: number): void {
    const g = this.ground(clientX, clientY) ?? this.goal.target;
    const d = MathUtils.clamp(this.goal.distance * factor, MIN_DISTANCE, MAX_DISTANCE);
    const k = d / this.goal.distance;
    this.goal.distance = d;
    this.goal.target.sub(g).multiplyScalar(k).add(g);
    this.goal.target.y = 0;
  }
  private panBy(dx: number, dy: number): void {
    const rect = this.dom.getBoundingClientRect();
    const perPx = (this.goal.distance * 2 * this.tanHalf) / Math.max(rect.height, 1);
    const a = this.goal.azimuth;
    // screen right = (cos a, 0, −sin a); screen up on the ground = away from the camera = (−sin a, 0, −cos a)
    const along = dy * perPx / Math.max(Math.sin(this.goal.elevation), 0.22);
    this.goal.target.x += -Math.cos(a) * dx * perPx - Math.sin(a) * along;
    this.goal.target.z += Math.sin(a) * dx * perPx - Math.cos(a) * along;
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    this.takeOver();
    const unit = e.deltaMode === 1 ? 32 : e.deltaMode === 2 ? 400 : 1;
    const delta = MathUtils.clamp(e.deltaY * unit, -240, 240) * (e.ctrlKey ? 3.2 : 1); // ctrl = trackpad pinch
    this.zoomAbout(Math.exp(delta * 0.0016), e.clientX, e.clientY);
  }
  private onDown(e: PointerEvent): void {
    this.dom.setPointerCapture?.(e.pointerId);
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, button: e.button, shift: e.shiftKey || e.ctrlKey || e.metaKey });
    this.pinch = null;
    if (e.pointerType === 'touch' && this.pointers.size === 1) {
      const now = performance.now();
      if (now - this.lastTap.t < 320 && Math.hypot(e.clientX - this.lastTap.x, e.clientY - this.lastTap.y) < 28) {
        this.flyTo(e.clientX, e.clientY);
        this.lastTap.t = 0;
        return;
      }
      this.lastTap = { t: now, x: e.clientX, y: e.clientY };
    }
  }
  private onMove(e: PointerEvent): void {
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    p.x = e.clientX;
    p.y = e.clientY;
    if (dx === 0 && dy === 0) return;
    if (this.pointers.size >= 2) {
      const [a, b] = [...this.pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const cx = (a.x + b.x) / 2;
      const cy = (a.y + b.y) / 2;
      if (this.pinch && dist > 1) {
        this.takeOver();
        this.zoomAbout(this.pinch.dist / dist, cx, cy);
        this.panBy(cx - this.pinch.cx, cy - this.pinch.cy);
      }
      this.pinch = { dist, cx, cy };
      return;
    }
    if (Math.hypot(dx, dy) > 0) this.lastTap.t = Math.hypot(e.clientX - this.lastTap.x, e.clientY - this.lastTap.y) > 12 ? 0 : this.lastTap.t;
    this.takeOver();
    if (p.button === 2 || p.button === 1 || p.shift) {
      this.panBy(dx, dy);
    } else {
      const rect = this.dom.getBoundingClientRect();
      const k = 2.6 / Math.max(rect.height, 1);
      this.goal.azimuth -= dx * k;
      this.goal.elevation = MathUtils.clamp(this.goal.elevation + dy * k, MIN_ELEVATION, MAX_ELEVATION);
    }
  }
  private onUp(e: PointerEvent): void {
    this.pointers.delete(e.pointerId);
    this.pinch = null;
  }

  /** Once per frame: gestures → damped pose → render camera. */
  update(aspect: number, now: number): void {
    const cam = this.camera;
    cam.aspect = aspect;
    const dt = Math.min(0.1, Math.max(0, (now - (this.lastUpdate || now)) / 1000));
    this.lastUpdate = now;
    let shift: number;
    if (this.flight) {
      const f = this.flight;
      const u = Math.min(1, (performance.now() - f.start) / f.duration);
      const e = ease(u);
      this.pose.target.lerpVectors(f.from.target, f.to.target, e);
      this.pose.azimuth = MathUtils.lerp(f.from.azimuth, f.to.azimuth, e);
      this.pose.elevation = MathUtils.lerp(f.from.elevation, f.to.elevation, e);
      this.pose.distance = Math.exp(MathUtils.lerp(Math.log(f.from.distance), Math.log(f.to.distance), e));
      if (u >= 1) {
        this.goal = clonePose(f.to);
        if (f.land && this.home) {
          this.explicit = { pos: this.home.pos, target: this.home.target, shift: this.home.shift };
          this.maxShift = Math.max(0, this.home.shift);
          if (this.moved) { this.moved = false; this.onMovedChange(false); }
        }
        this.flight = null;
        this.idle.since = performance.now();
      }
    } else if (!this.explicit || this.idle.base) {
      // exponential damping toward the goal: frame-rate independent
      const k = 1 - Math.exp(-dt * 9);
      const g = this.goal;
      g.target.set(MathUtils.clamp(g.target.x, -TARGET_LIMIT, TARGET_LIMIT), 0, MathUtils.clamp(g.target.z, -TARGET_LIMIT, TARGET_LIMIT));
      this.pose.target.lerp(g.target, k);
      this.pose.azimuth += (g.azimuth - this.pose.azimuth) * k;
      this.pose.elevation += (g.elevation - this.pose.elevation) * k;
      this.pose.distance *= Math.pow(g.distance / this.pose.distance, k);
    }
    // slow idle drift: a few degrees around wherever the camera rests
    if (this.idleDrift && !this.flight && this.pointers.size === 0 && performance.now() - this.idle.since > IDLE_AFTER_MS) {
      if (!this.idle.base) {
        this.idle.base = clonePose(this.pose);
        this.explicit = null;
      }
      const t = (performance.now() - this.idle.since - IDLE_AFTER_MS) / 1000;
      const gain = ease(Math.min(1, t / 8));
      this.goal.azimuth = this.idle.base.azimuth + MathUtils.degToRad(2.6) * gain * Math.sin(t * 0.085);
      this.goal.elevation = MathUtils.clamp(this.idle.base.elevation + MathUtils.degToRad(0.7) * gain * Math.sin(t * 0.061 + 1.3), MIN_ELEVATION, MAX_ELEVATION);
      this.goal.distance = this.idle.base.distance * (1 - 0.025 * gain * (0.5 - 0.5 * Math.cos(t * 0.05)));
    }

    if (this.explicit) {
      cam.position.copy(this.explicit.pos);
      cam.up.set(0, 1, 0);
      cam.lookAt(this.explicit.target);
      shift = this.explicit.shift;
    } else {
      const p = Rig.position(this.pose, cam.position);
      if (p.y < 5) p.y = 5; // never under water, never inside the quay
      const t = this.pose.target;
      const dx = t.x - p.x;
      const dz = t.z - p.z;
      const horizontal = Math.max(Math.hypot(dx, dz), 1e-6);
      const elevation = Math.atan2(p.y - t.y, horizontal);
      const fade = 1 - MathUtils.smoothstep(elevation, FADE_START, FADE_END);
      shift = Math.max(0, Math.min(this.maxShift * fade, Math.tan(elevation) / this.tanHalf));
      const pitch = elevation - Math.atan(shift * this.tanHalf);
      this.fwd.set((dx / horizontal) * Math.cos(pitch), -Math.sin(pitch), (dz / horizontal) * Math.cos(pitch));
      cam.up.set(0, 1, 0);
      cam.lookAt(this.tmp.copy(p).add(this.fwd));
    }
    this.shift = shift;
    this.near = MathUtils.clamp(this.pose.distance * 0.006, 0.5, 4);
    cam.near = this.near;
    cam.updateProjectionMatrix();
    // post-multiply the lens shift: clip.y += shift · clip.w  (clip.w = −z_view → element [row 1, col 2])
    cam.projectionMatrix.elements[9] -= shift;
    cam.projectionMatrixInverse.copy(cam.projectionMatrix).invert();
    cam.updateMatrixWorld(true);
  }
}

/* ------------------------------------------------------------------ boot */
const nextPaint = (): Promise<void> => new Promise((r) => requestAnimationFrame(() => setTimeout(r, 0)));

async function boot(): Promise<void> {
  const t0 = performance.now();
  if (!('gpu' in navigator)) {
    fail('This scene is drawn live with WebGPU, which this browser does not offer yet. It runs in Chrome, Edge, Arc, and Safari 26 or newer.');
    return;
  }
  progress('Fetching the renderer', 8);
  const base = import.meta.env.BASE_URL;
  const wasm = await import(/* @vite-ignore */ `${base}wasm/marinabay_web.js`);
  await wasm.default();
  // the world is built synchronously inside initMarinaBay: let the overlay paint first; the line keeps
  // shimmering meanwhile (a compositor-driven CSS animation — it does not need this thread)
  progress('Building Marina Bay…', 34);
  loading.classList.add('busy');
  await nextPaint();

  const freeze = num('freeze');
  const only = (params.get('only') ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  const dpr = renderDpr();
  const options = {
    dpr,
    msaaSamples: params.get('msaa') === '1' ? 1 : 4,
    timestamps: showStats || shot || params.get('timestamps') === '1',
    freeze,
    only: only.length ? only : null,
    quality,
    world: params.get('world') ?? '',
    sun: vec3('sun'),
    bloom: !off('bloom'),
    reflect: !off('reflect'),
    halos: !off('halos'),
    ao: params.has('ao') ? !off('ao') : null,
    cull: !off('cull'),
    lod: !off('lod'),
    stress: Math.max(1, Math.min(16, Math.round(num('stress') ?? 1))),
  };
  let engine: Engine;
  try {
    engine = await wasm.initMarinaBay(canvas, JSON.stringify(options));
  } catch (e) {
    fail(`WebGPU could not start on this device (${e instanceof Error ? e.message : String(e)}).`);
    return;
  }
  progress('Lighting the lamps…', 82);
  await nextPaint();

  // run-time tuning straight from the URL (A/B without a rebuild): every GradeParams knob + a few renderer ones
  const tune: Record<string, number | string> = {};
  for (const key of ['exposure', 'bloomThreshold', 'bloomKnee', 'bloomStrength', 'bloomSpread', 'bloomLevelGain', 'tonemapKnee', 'toe', 'saturation', 'vignette', 'shadowLift',
    'aoStrength', 'aoRadius', 'aoPower', 'haloGain', 'reflectHaloGain', 'minPx', 'reflectMinPx', 'thinPx', 'lodDensity', 'reflectLod', 'reflectStaticPx']) {
    const v = num(key);
    if (v !== null) tune[key] = v;
  }
  if (params.get('debug')) tune.debug = params.get('debug') as string;
  if (Object.keys(tune).length) engine.setOptions(JSON.stringify(tune));

  // ---- camera
  const hero = JSON.parse(engine.hero()) as Hero;
  const rig = new Rig(canvas);
  const view = params.get('view');
  const named = view && view !== 'hero' ? NAMED_VIEWS[view] : undefined;
  const cam = vec3('cam');
  if (cam) {
    rig.setExplicit(cam, vec3('target') ?? [0, 0, 0], num('fov') ?? hero.fov, num('shift') ?? 0);
  } else if (named) {
    rig.setExplicit(named.pos, named.target, num('fov') ?? named.fov ?? hero.fov, num('shift') ?? named.shift ?? 0);
  } else {
    rig.setExplicit(hero.pos, hero.target, num('fov') ?? hero.fov, num('shift') ?? hero.shiftY);
  }
  // any QA parameter means "show me exactly this": no intro, no idle drift
  const qa = Boolean(cam || view || freeze !== null || shot || showStats || params.get('debug') || params.has('stress') || params.has('nointro'));
  const cinematic = !qa && !reducedMotion;
  rig.idleDrift = cinematic;
  window.__marinabay = { engine, camera: rig.camera, controls: rig, stats: () => JSON.parse(engine.stats()) };

  resetButton.addEventListener('click', () => rig.reset());
  rig.onMovedChange = (moved) => resetButton.classList.toggle('show', moved);
  window.addEventListener('keydown', (e) => {
    if ((e.key === 'h' || e.key === 'H' || e.key === 'Home') && !e.metaKey && !e.ctrlKey && !e.altKey) rig.reset();
  });

  let cssW = 0;
  let cssH = 0;
  const resize = (): void => {
    cssW = Math.max(1, window.innerWidth);
    cssH = Math.max(1, window.innerHeight);
    engine.resize(cssW, cssH, renderDpr());
  };
  resize();
  window.addEventListener('resize', resize);
  window.addEventListener('orientationchange', () => setTimeout(resize, 120));
  window.visualViewport?.addEventListener('resize', resize);
  // a window dragged to another display changes devicePixelRatio without a resize event
  const watchDpr = (): void => matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`).addEventListener('change', () => { resize(); watchDpr(); }, { once: true });
  watchDpr();

  const view16 = new Float32Array(16);
  const pos3 = new Float32Array(3);
  const clock0 = performance.now();
  let frames = 0;
  let ready = false;
  let failed = false;
  let smoothedMs = 0;

  const frame = (now: number): void => {
    if (failed) return;
    requestAnimationFrame(frame);
    rig.update(cssW / cssH, now);
    view16.set(rig.camera.matrixWorldInverse.elements);
    pos3.set([rig.camera.position.x, rig.camera.position.y, rig.camera.position.z]);
    engine.setTime(freeze ?? (now - clock0) / 1000);
    engine.setCamera(view16, pos3, rig.camera.fov, rig.near, rig.shift);
    const json = engine.render(now);
    frames++;
    if (frames <= 12 || frames % 20 === 0 || showStats) {
      const s = JSON.parse(json);
      if (s.error) {
        failed = true;
        fail(`Rendering stopped: ${s.error}`);
        return;
      }
      if (showStats) {
        smoothedMs = smoothedMs ? smoothedMs * 0.92 + s.frameMs * 0.08 : s.frameMs;
        const g = s.gpu;
        const c = s.counters;
        statsEl.hidden = false;
        statsEl.textContent =
          `${(1000 / Math.max(smoothedMs, 0.01)).toFixed(0)} fps  ${smoothedMs.toFixed(1)} ms  cpu ${s.cpuMs.toFixed(2)} ms` +
          (g ? `\ngpu ${g.total.toFixed(2)} ms  (reflection ${g.reflection.toFixed(2)}  scene ${g.scene.toFixed(2)}  ao ${g.ao.toFixed(2)}  bloom ${g.bloom.toFixed(2)}  grade ${g.grade.toFixed(2)})` : '') +
          `\n${s.size[0]}×${s.size[1]} @${s.dpr}  msaa ${s.msaa}  ${s.settings.quality}  ${c.draw_calls} draws  ${(c.visible_triangles / 1e6).toFixed(2)} / ${(c.triangles / 1e6).toFixed(2)} M tris` +
          `\n${c.visible_instances} / ${c.instances} instances (mirror ${c.reflected_instances})  ${c.visible_chunks} / ${c.static_chunks} chunks  ${c.lights} lights` +
          `\nworld ${s.world.source} (${s.world.buildMs.toFixed(0)} ms)  shift ${rig.shift.toFixed(3)}`;
      }
    }
    if (!ready && frames === 3) {
      ready = true;
      // the overlay stays until a REAL frame has been executed by the GPU without an error
      engine.frameDone().then(() => {
        const s = JSON.parse(engine.stats());
        if (s.error || (s.diag && s.diag.length)) {
          failed = true;
          fail(`WebGPU reported: ${s.error ?? s.diag[0]}`);
          return;
        }
        progress('', 100);
        loading.classList.remove('busy');
        loading.classList.add('done');
        loading.setAttribute('aria-busy', 'false');
        if (cinematic) rig.startIntro(5000);
        window.__SCENE_READY_MS__ = performance.now() - t0;
        window.__sceneReady = true;
      }, (e) => fail(String(e)));
    }
  };
  requestAnimationFrame(frame);
}

boot().catch((e) => fail(e instanceof Error ? e.message : String(e)));
